import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '../../../i18n';
import { fmt } from '../data/metrics';
import type { ConnectedSourceId, Outlet } from '../types';
import { Icon } from './shared/Icon';

type ActionTone = 'alert' | 'win' | 'open' | 'info';

type OutletAction = {
  key: string;
  icon: string;
  tone: ActionTone;
  title: string;
  detail: string;
  cov: number;
};

const TONE_RANK: Record<ActionTone, number> = {
  alert: 0,
  win: 1,
  open: 2,
  info: 3,
};

// Concrete things to do tonight in THIS restaurant - all derived from real
// outlet fields. Deterministic; no simulation.
function outletActions(
  o: Outlet,
  connected: ConnectedSourceId[],
): OutletAction[] {
  const acts: OutletAction[] = [];
  const occ = o.seats > 0 ? o.seated / o.seats : 0;

  if (o.noShow >= 7 && occ >= 0.78) {
    const t = o.noShow >= 10 ? 3 : 2;
    acts.push({
      key: 'noshow',
      icon: 'flag',
      tone: 'alert',
      title: i18n.t('live:actions.noshowTitle', { count: t }),
      detail: i18n.t('live:actions.noshowDetail', { rate: o.noShow }),
      cov: t * 3,
    });
  }
  if (occ < 0.6) {
    acts.push({
      key: 'fill',
      icon: 'users',
      tone: 'open',
      title: i18n.t('live:actions.fillTitle'),
      detail: i18n.t('live:actions.fillDetail', { occ: Math.round(occ * 100) }),
      cov: Math.max(5, Math.round((0.75 - occ) * o.seats)),
    });
  }
  if (connected.includes('events') && o.events?.some(e => e.surge)) {
    const e = o.events.find(ev => ev.surge)!;
    acts.push({
      key: 'surge',
      icon: 'ticket',
      tone: 'win',
      title: i18n.t('live:actions.surgeTitle', { name: e.name }),
      detail: i18n.t('live:actions.surgeDetail', { dist: e.dist, start: e.start }),
      cov: 10,
    });
  }
  if (o.upcoming >= 8 && occ >= 0.85) {
    acts.push({
      key: 'large',
      icon: 'calendar',
      tone: 'alert',
      title: i18n.t('live:actions.largeTitle'),
      detail: i18n.t('live:actions.largeDetail', { count: o.upcoming }),
      cov: 8,
    });
  }
  if (connected.includes('weather') && o.weather) {
    if (o.weather.terrace && occ < 0.9) {
      acts.push({
        key: 'terrace',
        icon: 'sun',
        tone: 'win',
        title: i18n.t('live:actions.terraceTitle'),
        detail: i18n.t('live:actions.terraceDetail', {
          cond: o.weather.cond,
          temp: o.weather.temp,
        }),
        cov: 6,
      });
    } else if (!o.weather.terrace) {
      acts.push({
        key: 'indoor',
        icon: 'cloud',
        tone: 'info',
        title: i18n.t('live:actions.indoorTitle'),
        detail: i18n.t('live:actions.indoorDetail', { cond: o.weather.cond }),
        cov: 4,
      });
    }
  }
  if (o.cancelRate >= 9) {
    acts.push({
      key: 'cancel',
      icon: 'x',
      tone: 'alert',
      title: i18n.t('live:actions.cancelTitle'),
      detail: i18n.t('live:actions.cancelDetail', { rate: o.cancelRate }),
      cov: 5,
    });
  }
  if (o.avgSeatMin >= 96 && occ >= 0.85) {
    acts.push({
      key: 'turn',
      icon: 'clock',
      tone: 'info',
      title: i18n.t('live:actions.turnTitle'),
      detail: i18n.t('live:actions.turnDetail', { minutes: o.avgSeatMin }),
      cov: 6,
    });
  }

  return acts
    .sort((a, b) => TONE_RANK[a.tone] - TONE_RANK[b.tone])
    .slice(0, 5);
}

export function OutletActions({
  outlet,
  connected,
}: {
  outlet: Outlet;
  connected: ConnectedSourceId[];
}) {
  const { t } = useTranslation('live');
  const hasPos = connected.includes('pos');
  const acts = useMemo(
    () => outletActions(outlet, connected),
    [outlet, connected],
  );
  const [done, setDone] = useState<Record<string, boolean>>({});

  if (!acts.length) {
    return null;
  }

  const live = acts.filter(a => !done[a.key]);
  const upside = live.reduce((s, a) => s + a.cov, 0);
  const val = hasPos
    ? fmt.eur(upside * (outlet.spendCover ?? 0))
    : t('actions.guests', { count: fmt.num(upside) });
  const toneC: Record<ActionTone, string> = {
    alert: 'var(--st-alert)',
    win: 'var(--st-steady)',
    open: 'var(--accent)',
    info: 'var(--petrol-ink)',
  };

  return (
    <>
      <div className="sec-head" style={{ margin: '20px 0 4px' }}>
        <h2>{t('actions.title')}</h2>
        <span className="sh-meta">
          {live.length ? (
            t('actions.stillOnTable', {
              value: val,
              count: live.length,
              moves: t(live.length === 1 ? 'actions.move' : 'actions.moves'),
            })
          ) : (
            t('actions.allClear')
          )}
        </span>
      </div>
      <div className="oa-list">
        {acts.map(a => {
          const isDone = !!done[a.key];
          const chip = hasPos
            ? fmt.eur(a.cov * (outlet.spendCover ?? 0))
            : t('actions.cov', { count: a.cov });
          return (
            <div
              className={`oa-item${isDone ? ' done' : ''}`}
              key={a.key}
              onClick={() => setDone(d => ({ ...d, [a.key]: !d[a.key] }))}
            >
              <div
                className="oa-check"
                style={
                  isDone
                    ? {
                        background: 'var(--st-steady)',
                        borderColor: 'var(--st-steady)',
                      }
                    : undefined
                }
              >
                {isDone && (
                  <Icon name="check" size={13} stroke={3} style={{ color: '#fff' }} />
                )}
              </div>
              <div
                className="oa-ic"
                style={{ color: isDone ? 'var(--ink-3)' : toneC[a.tone] }}
              >
                <Icon name={a.icon} size={16} />
              </div>
              <div className="oa-main">
                <div className="oa-title">{a.title}</div>
                <div className="oa-detail">{a.detail}</div>
              </div>
              <div className="oa-chip" style={{ color: toneC[a.tone] }}>
                +{chip}
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
}
